import React, { useMemo, useState } from 'react';
import {
	Box,
	SimpleGrid,
	Badge,
	HStack,
	Text,
	Button,
	Flex,
	Icon,
	Progress,
	Input,
	InputGroup,
	InputLeftElement,
	VStack,
	useColorModeValue,
} from '@chakra-ui/react';
import {
	FaBullhorn,
	FaEye,
	FaEdit,
	FaChartLine,
	FaPaperPlane,
	FaMousePointer,
	FaEnvelopeOpen,
	FaSearch,
	FaPlus,
	FaClock,
	FaCheckCircle,
} from 'react-icons/fa';
import { FiSend } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { SectionCard, StatTile, EmptyPanel } from './dashboardShared';

const DISPLAY_LIMIT = 6;

const STATUS_META = {
	draft: { color: 'yellow', label: 'Draft', icon: FaEdit },
	scheduled: { color: 'blue', label: 'Scheduled', icon: FaClock },
	sending: { color: 'orange', label: 'Sending', icon: FiSend },
	sent: { color: 'green', label: 'Sent', icon: FaCheckCircle },
};

const getStatusMeta = (status) =>
	STATUS_META[String(status || '').toLowerCase()] || {
		color: 'purple',
		label: status || 'Unknown',
		icon: FaBullhorn,
	};

const CampaignMetric = ({ label, value, accent, icon }) => (
	<Box textAlign='center' flex={1}>
		<Icon as={icon} color={`${accent}.500`} boxSize={3.5} mb={1} />
		<Text fontSize='md' fontWeight='bold' color='gray.800' lineHeight='1'>
			{value}
		</Text>
		<Text fontSize='10px' color='gray.500' textTransform='uppercase' fontWeight='bold'>
			{label}
		</Text>
	</Box>
);

const DashboardCampaignCard = ({ campaign, onAnalytics, onEdit }) => {
	const cardBg = useColorModeValue('white', 'gray.800');
	const border = useColorModeValue('gray.200', 'gray.700');
	const statBg = useColorModeValue('gray.50', 'gray.700');
	const meta = getStatusMeta(campaign.status);

	const delivered = campaign.sent || 0;
	const openRate = delivered > 0 ? Math.round((campaign.opens / delivered) * 100) : 0;
	const clickRate = delivered > 0 ? Math.round((campaign.clicks / delivered) * 100) : 0;
	const isSent = campaign.status === 'sent' || delivered > 0;

	return (
		<Box
			bg={cardBg}
			border='1px solid'
			borderColor={border}
			borderRadius='2xl'
			overflow='hidden'
			transition='all 0.25s ease'
			_hover={{ transform: 'translateY(-3px)', boxShadow: 'lg' }}
			h='full'
			display='flex'
			flexDirection='column'>
			<Box h='4px' bgGradient={`linear(to-r, ${meta.color}.400, teal.400, purple.400)`} />

			<Box p={4} flex={1} display='flex' flexDirection='column'>
				<Flex justify='space-between' align='flex-start' gap={2} mb={3}>
					<Box minW={0} flex={1}>
						<Badge
							colorScheme={meta.color}
							borderRadius='full'
							px={2}
							py={0.5}
							mb={2}
							display='inline-flex'
							alignItems='center'
							gap={1}
							textTransform='capitalize'>
							<Icon as={meta.icon} boxSize={2.5} />
							{meta.label}
						</Badge>
						<Text fontWeight='bold' fontSize='md' color='gray.800' noOfLines={2} lineHeight='short'>
							{campaign.name}
						</Text>
						{campaign.subject && (
							<Text fontSize='xs' color='gray.500' mt={1} noOfLines={1}>
								{campaign.subject}
							</Text>
						)}
					</Box>
				</Flex>

				<HStack spacing={2} mb={3} fontSize='xs' color='gray.500'>
					<Text>Created {campaign.created}</Text>
					{campaign.recipients > 0 && (
						<>
							<Text>·</Text>
							<Text>{campaign.recipients.toLocaleString()} recipients</Text>
						</>
					)}
				</HStack>

				<Flex
					bg={statBg}
					borderRadius='xl'
					p={3}
					mb={3}
					gap={2}
					justify='space-between'>
					<CampaignMetric
						label='Sent'
						value={campaign.sent.toLocaleString()}
						accent='blue'
						icon={FaPaperPlane}
					/>
					<CampaignMetric
						label='Opens'
						value={campaign.opens.toLocaleString()}
						accent='teal'
						icon={FaEnvelopeOpen}
					/>
					<CampaignMetric
						label='Clicks'
						value={campaign.clicks.toLocaleString()}
						accent='purple'
						icon={FaMousePointer}
					/>
				</Flex>

				{isSent && delivered > 0 && (
					<Box mb={3}>
						<Flex justify='space-between' mb={1}>
							<Text fontSize='xs' color='gray.500'>
								Open rate
							</Text>
							<Text fontSize='xs' fontWeight='semibold' color='teal.600'>
								{openRate}%
							</Text>
						</Flex>
						<Progress
							value={openRate}
							size='xs'
							borderRadius='full'
							colorScheme='teal'
							bg='gray.100'
							mb={2}
						/>
						<Flex justify='space-between' mb={1}>
							<Text fontSize='xs' color='gray.500'>
								Click rate
							</Text>
							<Text fontSize='xs' fontWeight='semibold' color='purple.600'>
								{clickRate}%
							</Text>
						</Flex>
						<Progress
							value={clickRate}
							size='xs'
							borderRadius='full'
							colorScheme='purple'
							bg='gray.100'
						/>
					</Box>
				)}

				<Flex gap={2} mt='auto' flexWrap='wrap'>
					<Button
						size='sm'
						flex={1}
						colorScheme='teal'
						leftIcon={<FaChartLine />}
						borderRadius='xl'
						onClick={() => onAnalytics(campaign.id)}
						isDisabled={!isSent && campaign.status !== 'sent'}>
						Analytics
					</Button>
					<Button
						size='sm'
						flex={1}
						variant='outline'
						leftIcon={<FaEdit />}
						borderRadius='xl'
						onClick={() => onEdit(campaign.id)}>
						Edit
					</Button>
				</Flex>
			</Box>
		</Box>
	);
};

const CampaignsTab = ({ campaigns = [] }) => {
	const navigate = useNavigate();
	const [search, setSearch] = useState('');

	const filtered = useMemo(() => {
		const term = search.trim().toLowerCase();
		if (!term) return campaigns;
		return campaigns.filter(
			(c) =>
				c.name?.toLowerCase().includes(term) ||
				c.subject?.toLowerCase().includes(term) ||
				c.status?.toLowerCase().includes(term),
		);
	}, [campaigns, search]);

	const displayCampaigns = filtered.slice(0, DISPLAY_LIMIT);
	const hasMore = filtered.length > DISPLAY_LIMIT;

	const totals = useMemo(() => {
		const sent = campaigns.reduce((sum, c) => sum + (c.sent || 0), 0);
		const opens = campaigns.reduce((sum, c) => sum + (c.opens || 0), 0);
		const clicks = campaigns.reduce((sum, c) => sum + (c.clicks || 0), 0);
		const sentCampaigns = campaigns.filter((c) => (c.sent || 0) > 0);
		const avgOpen =
			sentCampaigns.length > 0
				? Math.round(
						sentCampaigns.reduce((sum, c) => {
							const rate = c.sent > 0 ? (c.opens / c.sent) * 100 : 0;
							return sum + rate;
						}, 0) / sentCampaigns.length,
					)
				: 0;

		return { sent, opens, clicks, avgOpen };
	}, [campaigns]);

	return (
		<VStack spacing={5} align='stretch'>
			<SimpleGrid columns={{ base: 1, sm: 2, lg: 4 }} spacing={4}>
				<StatTile
					title='Campaigns'
					value={campaigns.length}
					subtitle='Total in workspace'
					icon={FaBullhorn}
					gradient='linear(135deg, purple.400, purple.600)'
					bgGradient='linear(purple.400, purple.600)'
					onClick={() => navigate('/campaigns')}
				/>
				<StatTile
					title='Emails sent'
					value={totals.sent.toLocaleString()}
					subtitle='Delivered recipients'
					icon={FaPaperPlane}
					gradient='linear(135deg, blue.400, blue.600)'
					bgGradient='linear(blue.400, blue.600)'
				/>
				<StatTile
					title='Total opens'
					value={totals.opens.toLocaleString()}
					subtitle={totals.sent > 0 ? `${totals.avgOpen}% avg open rate` : 'No sends yet'}
					icon={FaEnvelopeOpen}
					gradient='linear(135deg, teal.400, teal.600)'
					bgGradient='linear(teal.400, teal.600)'
				/>
				<StatTile
					title='Total clicks'
					value={totals.clicks.toLocaleString()}
					subtitle='Link engagement'
					icon={FaMousePointer}
					gradient='linear(135deg, orange.400, orange.600)'
					bgGradient='linear(orange.400, orange.600)'
				/>
			</SimpleGrid>

			<SectionCard
				title='Email campaigns'
				description='Recent campaigns with delivery and engagement at a glance.'
				action={
					<HStack spacing={2} flexWrap='wrap'>
						<Button
							leftIcon={<FaEye />}
							size='sm'
							variant='outline'
							onClick={() => navigate('/campaigns')}>
							View all
						</Button>
						<Button
							leftIcon={<FaPlus />}
							size='sm'
							colorScheme='teal'
							onClick={() => navigate('/campaigns/create')}>
							Create campaign
						</Button>
					</HStack>
				}>
				{campaigns.length === 0 ? (
					<EmptyPanel
						message='No campaigns yet. Create your first email campaign to start tracking sends, opens, and clicks.'
						actionLabel='Create campaign'
						onAction={() => navigate('/campaigns/create')}
					/>
				) : (
					<VStack align='stretch' spacing={4}>
						<InputGroup size='sm' maxW={{ base: '100%', md: '320px' }}>
							<InputLeftElement pointerEvents='none'>
								<Icon as={FaSearch} color='gray.400' />
							</InputLeftElement>
							<Input
								placeholder='Search campaigns...'
								value={search}
								onChange={(e) => setSearch(e.target.value)}
								borderRadius='xl'
								bg='gray.50'
							/>
						</InputGroup>

						{filtered.length === 0 ? (
							<Box py={10} textAlign='center'>
								<Icon as={FaSearch} boxSize={8} color='gray.300' mb={3} />
								<Text fontWeight='semibold' color='gray.600'>
									No campaigns match your search
								</Text>
							</Box>
						) : (
							<>
								<SimpleGrid
									columns={{ base: 1, md: 2, xl: 3 }}
									spacing={4}>
									{displayCampaigns.map((campaign) => (
										<DashboardCampaignCard
											key={campaign.id}
											campaign={campaign}
											onAnalytics={(id) => navigate(`/campaigns/${id}/analytics`)}
											onEdit={(id) => navigate(`/campaigns/${id}/edit`)}
										/>
									))}
								</SimpleGrid>

								{hasMore && (
									<Flex justify='center' pt={2}>
										<Button
											variant='ghost'
											colorScheme='teal'
											size='sm'
											onClick={() => navigate('/campaigns')}>
											View all {filtered.length} campaigns
										</Button>
									</Flex>
								)}
							</>
						)}
					</VStack>
				)}
			</SectionCard>
		</VStack>
	);
};

export default CampaignsTab;
